import { useMemo } from 'react';

import { StoreItemModalsProps } from '#components/organisms/modals/StoreItemModals/StoreItemModals.js';
import { TicketedEventsModalsCard } from '#components/organisms/modals/TicketedEventsModals.js';
import {
  EventStoreItem,
  TicketStoreItem,
  useListEventDateTicketInventories,
  useListTickets,
  useShowEvent,
} from '@givebacks/api/store';
import { Box, ModalProps, Skeleton, Stack, Title } from 'ui';
import { useInViewport } from 'ui/Hooks';

import { CheckoutButtonsProps } from '../Cart/buttons/CheckoutButtons/CheckoutButtons';
import { EventDateTimeCard } from '../EventDateTimeCard/EventDateTimeCard';

type TicketsSectionProps = {
  eventPublishedAt?: string;
  eventId: string;
  causeId: string;
  eventDateId: string;
  title?: React.ReactNode;
  modalProps?: Partial<Omit<ModalProps, 'opened' | 'onClose' | 'title'>>;
  checkoutButtonsProps?: Partial<CheckoutButtonsProps>;
  additionalFieldsProps?: StoreItemModalsProps['additionalFieldsProps'];
};

type TicketCardProps = {
  eventId: string;
  causeId: string;
  eventDateId: string;
  ticket: TicketStoreItem;
  event: EventStoreItem;
  /** Determines if the parent section is visible, used for lazy loading the event date inventories */
  isSectionVisible: boolean;
} & Pick<TicketsSectionProps, 'modalProps' | 'checkoutButtonsProps' | 'additionalFieldsProps'>;

const TicketCard = ({
  eventId,
  causeId,
  eventDateId,
  ticket,
  modalProps,
  checkoutButtonsProps,
  additionalFieldsProps,
  event,
  isSectionVisible,
}: TicketCardProps) => {
  const { data: eventDate, isLoading: isLoadingEventDate } = useListEventDateTicketInventories({
    eventId,
    causeId,
    dateId: eventDateId,
    enabled: isSectionVisible,
  });

  const inventoryMap = useMemo(() => {
    const entries = eventDate?.event_date_tickets?.map?.((ticket) => {
      return [
        ticket.store_item.uuid,
        {
          currentInventory: ticket.inventory?.current_available,
          unlimited: ticket.store_item?.content?.unlimited ?? false,
        },
      ] as readonly [string, { currentInventory?: number; unlimited: boolean }];
    });

    return new Map(entries);
  }, [eventDate]);

  const { currentInventory, unlimited } = useMemo(
    () => inventoryMap.get(ticket.uuid) ?? { currentInventory: undefined, unlimited: false },
    [inventoryMap, ticket.uuid]
  );

  return (
    <Skeleton visible={isLoadingEventDate}>
      <TicketedEventsModalsCard
        storeId={causeId ?? ''}
        storeItemId={ticket.uuid ?? ''}
        eventId={event?.uuid ?? ''}
        eventTitle={event?.name ?? ''}
        title={ticket.name}
        description={ticket.description}
        price={ticket.price}
        eventDate={eventDate?.starting_at}
        eventDateId={eventDateId ?? ''}
        sellDate={ticket.starting_at ?? undefined}
        inventoryAvailable={event?.unlimited && unlimited ? false : currentInventory}
        eventDateTimeCard={(props) => <EventDateTimeCard {...props} />}
        checkoutButtonsProps={checkoutButtonsProps}
        {...modalProps}
        additionalFieldsProps={additionalFieldsProps}
      />
    </Skeleton>
  );
};

const TicketsSection = ({
  eventId,
  causeId,
  eventDateId,
  title,
  modalProps,
  checkoutButtonsProps,
  additionalFieldsProps,
}: TicketsSectionProps) => {
  const { ref, inViewport } = useInViewport();
  const { data: event } = useShowEvent({ eventId, causeId });
  const { data: tickets, isLoading: isLoading } = useListTickets({
    eventId,
    causeId,
    ticketsType: 'tickets',
    queryOptions: {
      join: 'AND',
      searches: {
        'event_date_tickets.event_date_id': {
          value: eventDateId,
        },
        live: {
          value: true,
        },
      },
      order: {
        field: 'order',
        direction: 'asc',
      },
      options: {
        include: 'inventory',
      },
    },
  });

  const ticketsList = useMemo(() => {
    if (!tickets?.store_items?.length || !event) return [];

    return tickets?.store_items?.map((ticket) => {
      return (
        <TicketCard
          key={ticket.uuid}
          ticket={ticket}
          eventId={eventId}
          causeId={causeId}
          eventDateId={eventDateId}
          modalProps={modalProps}
          checkoutButtonsProps={checkoutButtonsProps}
          additionalFieldsProps={additionalFieldsProps}
          event={event}
          isSectionVisible={inViewport}
        />
      );
    });
  }, [
    tickets?.store_items,
    event,
    eventId,
    causeId,
    eventDateId,
    modalProps,
    checkoutButtonsProps,
    additionalFieldsProps,
    inViewport,
  ]);

  return (
    <Stack gap="md" ref={ref}>
      <Skeleton visible={isLoading}>{title ?? <Title order={2}>Tickets</Title>}</Skeleton>
      <Stack>{ticketsList}</Stack>
    </Stack>
  );
};

type TicketsSectionGuardProps = Partial<TicketsSectionProps>;

/** Guards against rendering the ticket section if there are no tickets  */
const TicketsSectionGuard = ({
  eventId,
  causeId,
  eventDateId,
  ...props
}: TicketsSectionGuardProps) => {
  const { ref, inViewport } = useInViewport();
  const {
    data: tickets,
    isLoading: isLoadingTickets,
    status,
  } = useListTickets({
    eventId,
    causeId,
    ticketsType: 'tickets',
    queryOptions: {
      join: 'AND',
      searches: {
        'event_date_tickets.event_date_id': {
          value: eventDateId,
        },
        live: {
          value: true,
        },
      },
      order: {
        field: 'order',
        direction: 'asc',
      },
      options: {
        include: 'inventory',
      },
    },
    enabled: inViewport,
  });

  // Only return null if the tickets are loaded and there are no tickets
  if (status === 'success' && !tickets?.store_items?.length) {
    return null;
  }

  return (
    <Box ref={ref}>
      {!tickets?.store_items?.length || !eventId || !causeId || !eventDateId ? (
        <Skeleton visible={isLoadingTickets} h="80px" />
      ) : (
        <TicketsSection eventId={eventId} causeId={causeId} eventDateId={eventDateId} {...props} />
      )}
    </Box>
  );
};

export { TicketsSectionGuard as TicketsSection };
